import React, { useEffect, useState } from 'react';
import { Row, Stack, Text, PillButton, TextInput } from 'library/ui';
import { client, consoleLogInDev } from 'shared';

const INVALID_CODE_ERR = 'The code you entered is incorrect.';
const CODE_REQUEST_ERR = 'There was an error sending the verification code. Please try again later.';

export default function PhoneNumberVerification({
    phoneNumber,
    handleSuccess,
}: {
    phoneNumber: string;
    handleSuccess: (token: string) => void;
}) {
    const [code, setCode] = useState('');
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [invalidCode, setInvalidCode] = useState(false);
    const [attempts, setAttempts] = useState(0);
    const [errorText, setErrorText] = useState('');
    const [display, setDisplay] = useState('');

    useEffect(() => {
        const cleaned = ('' + phoneNumber).replace(/\D/g, '');
        // eslint-disable-next-line regexp/no-useless-quantifier
        const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            setDisplay(['(', match[2], ') ', match[3], '-', match[4]].join(''));
        }
        sendCode();
    }, []);

    useEffect(() => {
        if (code.length === 4 && !invalidCode && attempts < 6) {
            setButtonDisabled(false);
        } else {
            setButtonDisabled(true);
        }
    }, [code, invalidCode, attempts]);

    function verifyCode() {
        setAttempts(attempts + 1);
        setErrorText('');
        client(`check-verification-code/?code=${code}&receiver=${display.replace(/\D/g, '')}`, { method: 'POST' })
            .then((resp) => {
                if (resp.token) localStorage.setItem('token', resp.token);
                handleSuccess(resp.token);
            })
            .catch((err) => {
                consoleLogInDev(err);
                setInvalidCode(true);
                setErrorText(INVALID_CODE_ERR);
                resetValues();
            });
    }

    function sendCode() {
        setAttempts(attempts + 1);
        setErrorText('');
        if (attempts < 5) {
            client(`send-verification-text/?receiver=${phoneNumber}`, {
                method: 'POST',
            })
                .then(() => setErrorText(''))
                .catch((err) => {
                    consoleLogInDev(err);
                    setErrorText(CODE_REQUEST_ERR);
                    resetValues();
                });
        }
    }

    const resetValues = () => {
        setButtonDisabled(true);
        setInvalidCode(false);
        setCode('');
    };

    function onFormSubmit(event: React.FormEvent) {
        event.preventDefault();
        if (buttonDisabled) return;
        verifyCode();
    }

    return (
        <form onSubmit={onFormSubmit}>
            <Stack gap="lg">
                <Text size="sm">
                    Please enter the verification code sent to {display} to finish setting up your Tandem account.
                </Text>
                {attempts > 5 ? (
                    <Text size="sm" tone="error">
                        Maximum attempts exceeded. Please contact support@join-tandem.com for help.
                    </Text>
                ) : (
                    <TextInput
                        id="code"
                        value={code}
                        onChange={(value) => setCode(value.slice(0, 4))}
                        error={errorText}
                        fullWidth
                    />
                )}
                <Row gap="sm" wrap>
                    <PillButton
                        variant="secondary"
                        disabled={attempts > 5}
                        id="sign-up-resend-code-button"
                        onClick={() => sendCode()}
                    >
                        Resend Code
                    </PillButton>
                    <PillButton id="signup-finish-button" type="submit" disabled={buttonDisabled}>
                        Finish
                    </PillButton>
                </Row>
            </Stack>
        </form>
    );
}
